<template>
    <BaseModal :title="'Add Credit'" @close="closeModal" :visible="true">
        <form @submit.prevent="onSubmit">
            <div class="rlms-flex rlms-flex-col rlms-gap-30">
                <div class="pp-body-scroll">

                    <div class="rlms-flex rlms-flex-col rlms-gap-15">
                        <!-- Product -->
                        <div class="rlms-flex rlms-flex-col">
                            <label for="product" class="rlms-fs-xs rlms-fw-500">Product</label>
                            <select v-model="product">
                                <option value="">Select Product</option>
                                <option v-for="p in products" :key="p.id" :value="p.id">
                                    {{ p.name }}
                                </option>
                            </select>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && productError">
                                {{ productError }}
                            </span>
                        </div>

                        <!-- Quantity -->
                        <div class="rlms-flex rlms-flex-col">
                            <label for="qty" class="rlms-fs-xs rlms-fw-500">Quantity</label>
                            <input type="number" min="1" placeholder="Quantity" v-model.number="qty" />
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && qtyError">
                                {{ qtyError }}
                            </span>
                        </div>

                        <!-- Note -->
                        <div class="rlms-flex rlms-flex-col">
                            <label for="note" class="rlms-fs-xs rlms-fw-500">Note</label>
                            <textarea
                                placeholder="Reason for adding credit (e.g., Promotional credit, Customer service compensation, etc.)"
                                v-model.trim="note" style="resize: none; min-height: 120px;"></textarea>
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && noteError">
                                {{ noteError }}
                            </span>
                        </div>

                        <div class="credit-summary" v-if="productName">
                            <h3>Credit Summary</h3>
                            <p><strong>Product:</strong> {{ productName }}</p>
                            <p><strong>Quantity:</strong> {{ qty }}</p>
                            <p><strong>Total Value:</strong> $0.00 (Admin Credit)
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-between" style="padding: 0;">
                <button class="rlms-btn rlms-btn-outline-primary p-cancel-btn" type="button" id="cancel-org-modal"
                    @click="closeModal">
                    Cancel
                </button>
                <button type="submit" class="rlms-btn rlms-btn-primary" id="save-org-btn" @click="onSubmit">
                    Save
                </button>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import BaseModal from './BaseModal.vue';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';
import { showToast } from '@/utils/swal';

const loader = useLoaderStore();
const emit = defineEmits(['close', 'saved']);
const props = defineProps({
    organizationId: {
        type: Number,
        required: true,
    },
});

if (!props.organizationId || typeof props.organizationId !== 'number') {
    throw new Error('orgId prop is required');
}

const showModal = ref(true);
const submitting = ref(false);
const submitted = ref(false);
const products = ref([]);

// Close modal
const closeModal = () => {
    showModal.value = false;
    emit('close');
};

// Validation schema
const formSchema = yup.object({
    product: yup.string().required('Please select a product.'),
    qty: yup.number().min(1, 'Quantity must be at least 1').required('Please enter quantity.'),
    note: yup.string().optional(),
});

const { handleSubmit, setErrors } = useForm({
    validationSchema: formSchema,
    initialValues: {
        product: '',
        qty: 1,
        note: '',
    }
});

// Fields
const { value: product, errorMessage: productError } = useField('product');
const { value: qty, errorMessage: qtyError } = useField('qty');
const { value: note, errorMessage: noteError } = useField('note');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;
        try {
            loader.show();

            await api.post(`/admin/organizations/${props.organizationId}/add-credit`, { ...values });

            showToast({
                icon: 'success',
                title: 'Great! ',
                text: 'Credit added successfully!',
            });

            emit('saved');

            closeModal();
        } catch (error) {
            if (error?.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach((key) => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};

// Fetch dropdowns
const getProducts = async () => {
    try {
        loader.show();
        const { data } = await api.get('/products');
        products.value = data.data;
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

onMounted(async () => {
    await getProducts();
});

const productName = computed(() => {
    const prod = products.value.find(p => p.id === product.value);
    return prod ? prod.name : '';
});
</script>
<style scoped>
.credit-summary {
    background-color: #f5f5f5;
    padding: 15px;
    border-radius: 16px;
    margin: 15px 0;
}

.credit-summary h3 {
    margin: 0 0 10px;
    font-size: 16px;
}

.credit-summary p {
    margin: 5px 0;
    font-size: 14px;
}
</style>